import type {
  Activity,
  AgentSession,
  CreateNodeInput,
  DailyFocus,
  Node,
  NodeListQuery,
  NodeType,
  Note,
  NoteKind,
  NoteVersion,
  PermissionAction,
  PermissionRequest,
  RitualSchedule,
  RoutineOccurrence,
  SavedView,
  Settings,
  UpdateNodeInput,
  User,
} from '@goalbuddy/core';

/** Dates arrive as ISO strings over JSON. */
type Wire<T> = { [K in keyof T]: T[K] extends Date ? string : T[K] extends Date | null ? string | null : T[K] };

export type ApiNode = Wire<Node> & { tags: string[]; flags: string[] };
export type ApiNote = Omit<Wire<Note>, 'id' | 'updated_at'> & { id: number | null; updated_at: string | null };
export type ApiActivity = Wire<Activity> & { actor: string | null; actor_kind: 'human' | 'agent' | null; node_title?: string | null };
export type ApiUser = Pick<User, 'id' | 'kind' | 'name' | 'display_name' | 'role'>;
export type Me = ApiUser & { via: 'token' | 'session' };
export type ApiSchedule = Wire<RitualSchedule>;
export type ApiOccurrence = Wire<RoutineOccurrence>;
export type ApiPermission = Wire<PermissionRequest> & { requested_by_name: string | null };
export type ApiView = Wire<SavedView>;
export type ApiSession = Wire<AgentSession> & { user_name: string | null };
export interface RoutineStats {
  streak: number;
  done: number;
  due: number;
  adherence: number | null;
  window_from: string;
  window_to: string;
}
export interface FocusDay {
  date: string;
  confirmed: boolean;
  items: { rank: number; confirmed: boolean; suggested_by: number | null; node: ApiNode }[];
}
export interface FocusSuggestion {
  node_id: number;
  title: string;
  score: number;
  reasons: string[];
}
export interface OverviewItem {
  id: number;
  t: NodeType;
  p: number | null;
  title: string;
  s?: string;
  due?: string;
  period?: string;
  who?: string;
  f?: string[];
}
export interface BuddyItem {
  id: number;
  type: NodeType;
  title: string;
  status: string;
  check_cadence: string | null;
  next_check_at: string | null;
  buddy_note: string | null;
  due_date: string | null;
}
export interface Question {
  id: number;
  type: NodeType;
  title: string;
  parent_id: number | null;
  needs_clarification: boolean;
  open_question: string | null;
  missing_completion_criterion: boolean;
}
export interface ApiToken {
  id: number;
  user_id: number;
  user_name: string;
  label: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}
export interface UpcomingRitual {
  schedule_id: number;
  type: 'daily_prep' | 'weekly_review';
  at: string;
  overridden: boolean;
}

export interface HealthCheck {
  key: string;
  label: string;
  suggestion: string;
  items: { id: number; type: string; title: string; detail?: string }[];
}
export interface DataHealth {
  today: string;
  total: number;
  checks: HealthCheck[];
}
export interface ReviewTemplate {
  id: number;
  type: 'daily_prep' | 'weekly_review';
  version: number;
  questions: { key: string; question: string; hint?: string }[];
  notes: string | null;
  created_at: string;
}
export interface ApiReview {
  id: number;
  type: 'daily_prep' | 'weekly_review';
  date: string;
  template_id: number | null;
  template_version?: number | null;
  answers: Record<string, unknown>;
  summary_md: string | null;
  insights_next_time: string | null;
  created_at: string;
}
export interface DailyPrep {
  date: string;
  tomorrow: string;
  template: ReviewTemplate;
  done_today: ApiNode[];
  focus_today: ApiNode[];
  focus_open: ApiNode[];
  to_move: ApiNode[];
  tomorrow_calendar: { scheduled: ApiNode[]; due: ApiNode[]; rituals: UpcomingRitual[] };
  buddy_due: { id: number; type: string; title: string; buddy_note: string | null; next_check_at: string | null }[];
  focus_suggestion: FocusSuggestion[];
  focus_tomorrow: FocusDay;
}
export interface WeeklyPrep {
  week_from: string;
  week_to: string;
  template: ReviewTemplate;
  done_count: number;
  done_by_area: { area: string; goal: string | null; items: { id: number; type: string; title: string }[] }[];
  routine_adherence: { id: number; title: string; week: { done: number; due: number }; last_30_days: { done: number; due: number }; streak: number }[];
  health: DataHealth;
  buddy_focus: { id: number; type: string; title: string; status: string; next_check_at: string | null; buddy_note: string | null }[];
  proposals: string[];
  last_insights: { date: string; insights_next_time: string | null } | null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

export interface ClientOptions {
  /** Base URL including `/api`, e.g. `https://goalbuddy.devotron.de/api` or `/api` in the browser. */
  baseUrl: string;
  token?: string;
  /** Called on 401 (e.g. to redirect to the login page). */
  onUnauthorized?: () => void;
  fetch?: typeof fetch;
}

type Query = Record<string, string | number | boolean | string[] | undefined | null>;

function qs(q?: Query): string {
  if (!q) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export type NodeQuery = { [K in keyof NodeListQuery]?: NodeListQuery[K] extends Date | undefined ? string : NodeListQuery[K] | string };

export function createClient(opts: ClientOptions) {
  const f = opts.fetch ?? globalThis.fetch.bind(globalThis);

  async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (opts.token) headers.authorization = `Bearer ${opts.token}`;
    const res = await f(opts.baseUrl.replace(/\/$/, '') + path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
    });
    const text = await res.text();
    let json: unknown;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      throw new ApiError(res.status, 'invalid_response', `Unexpected response (${res.status}): ${text.slice(0, 200)}`);
    }
    if (!res.ok) {
      if (res.status === 401) opts.onUnauthorized?.();
      const e = (json as { error?: { code: string; message: string; details?: unknown } } | null)?.error;
      throw new ApiError(res.status, e?.code ?? 'http_error', e?.message ?? `HTTP ${res.status}`, e?.details);
    }
    return json as T;
  }

  const get = <T>(p: string, q?: Query) => req<T>('GET', p + qs(q));
  const post = <T>(p: string, b: unknown = {}) => req<T>('POST', p, b);

  return {
    request: req,
    health: () => get<{ status: string; db: boolean; time: string }>('/health'),

    login: (password: string, name = 'andreas') => post<{ user: ApiUser }>('/auth/login', { name, password }),
    logout: () => post<{ ok: true }>('/auth/logout'),
    me: () => get<{ user: Me }>('/auth/me').then((r) => r.user),

    users: () => get<{ users: ApiUser[] }>('/users').then((r) => r.users),
    tokens: () => get<{ tokens: ApiToken[] }>('/tokens').then((r) => r.tokens),
    createToken: (input: { user_name?: string; user_id?: number; label: string }) =>
      post<{ id: number; user_name: string; label: string; token: string }>('/tokens', input),
    revokeToken: (id: number) => req<{ ok: true }>('DELETE', `/tokens/${id}`),

    nodes: (q?: NodeQuery) => get<{ nodes: ApiNode[] }>('/nodes', q as Query).then((r) => r.nodes),
    node: (id: number) => get<{ node: ApiNode }>(`/nodes/${id}`).then((r) => r.node),
    createNode: (input: CreateNodeInput) => post<{ node: ApiNode }>('/nodes', input).then((r) => r.node),
    updateNode: (id: number, input: Partial<Omit<UpdateNodeInput, 'scheduled_at' | 'next_check_at'>> & { scheduled_at?: string | null; next_check_at?: string | null }) =>
      req<{ node: ApiNode }>('PATCH', `/nodes/${id}`, input).then((r) => r.node),
    dropNode: (id: number) => req<{ node: ApiNode }>('DELETE', `/nodes/${id}`).then((r) => r.node),
    moveNode: (id: number, parent_id: number | null, sort?: number) =>
      post<{ node: ApiNode }>(`/nodes/${id}/move`, { parent_id, sort }).then((r) => r.node),
    reorderNode: (id: number, sort: number) => post<{ node: ApiNode }>(`/nodes/${id}/reorder`, { sort }).then((r) => r.node),
    convertNode: (id: number, type: 'task' | 'goal' | 'result') =>
      post<{ node: ApiNode }>(`/nodes/${id}/convert`, { type }).then((r) => r.node),
    doneNode: (id: number, message?: string) => post<{ node: ApiNode }>(`/nodes/${id}/done`, { message }).then((r) => r.node),
    archiveNode: (id: number) => post<{ ids: number[] }>(`/nodes/${id}/archive`),
    restoreNode: (id: number) => post<{ ids: number[] }>(`/nodes/${id}/restore`),
    assignNode: (id: number, input: { assignee_type: 'human' | 'agent' | null; assignee_id?: number | null; assignee?: string }) =>
      post<{ node: ApiNode }>(`/nodes/${id}/assign`, input).then((r) => r.node),
    setBuddy: (id: number, input: { buddy_focus: boolean; check_cadence?: string | null; buddy_note?: string | null; next_check_at?: string | null }) =>
      post<{ node: ApiNode; buddy_focus_count: number; buddy_focus_limit: number; warning: string | null }>(`/nodes/${id}/buddy`, input),
    buddyChecked: (id: number, next_check_at: string, message: string) =>
      post<{ node: ApiNode }>(`/nodes/${id}/buddy/checked`, { next_check_at, message }).then((r) => r.node),
    addTags: (id: number, tags: string[]) => post<{ added: string[]; node: ApiNode }>(`/nodes/${id}/tags`, { tags }),
    removeTag: (id: number, tag: string) => req<{ removed: boolean; node: ApiNode }>('DELETE', `/nodes/${id}/tags/${encodeURIComponent(tag)}`),
    context: (id: number) =>
      get<{ node: ApiNode; ancestors: ApiNode[]; children: ApiNode[]; notes: { human: ApiNote; agent: ApiNote }; activity: ApiActivity[] }>(
        `/nodes/${id}/context`,
      ),

    note: (id: number, kind: NoteKind) => get<{ note: ApiNote }>(`/nodes/${id}/notes/${kind}`).then((r) => r.note),
    putNote: (id: number, kind: NoteKind, body_md: string, base_version: number) =>
      req<{ note: ApiNote }>('PUT', `/nodes/${id}/notes/${kind}`, { body_md, base_version }).then((r) => r.note),
    noteVersions: (id: number, kind: NoteKind) =>
      get<{ versions: Wire<NoteVersion>[] }>(`/nodes/${id}/notes/${kind}/versions`).then((r) => r.versions),

    overview: () => get<{ legend: string; today: string; count: number; nodes: OverviewItem[] }>('/overview'),
    tags: () => get<{ tags: { name: string; count: number }[] }>('/tags').then((r) => r.tags),
    buddy: (due = false) => get<{ buddy_focus_limit: number; count: number; items: BuddyItem[] }>('/buddy', { due }),
    questions: () => get<{ questions: Question[] }>('/questions').then((r) => r.questions),
    activity: (q?: { actor?: string; node?: number; since?: string; kind?: 'human' | 'agent'; limit?: number }) =>
      get<{ activity: ApiActivity[] }>('/activity', q).then((r) => r.activity),
    log: (message: string, node_id?: number) => post<{ ok: true }>('/log', { message, node_id }),

    settings: () => get<{ settings: Settings }>('/settings').then((r) => r.settings),
    updateSettings: (patch: Partial<Settings>) => req<{ settings: Settings }>('PATCH', '/settings', patch).then((r) => r.settings),

    schedules: () => get<{ schedules: ApiSchedule[] }>('/schedules').then((r) => r.schedules),
    upcoming: (days = 14) => get<{ upcoming: UpcomingRitual[] }>('/schedules/upcoming', { days }).then((r) => r.upcoming),
    updateSchedule: (id: number, patch: { rrule?: string; time?: string; timezone?: string; next_run_at?: string | null; enabled?: boolean }) =>
      req<{ schedule: ApiSchedule }>('PATCH', `/schedules/${id}`, patch).then((r) => r.schedule),

    focus: (date?: string) => get<FocusDay>('/focus', { date }),
    suggestFocus: (date?: string, save = false) => post<{ date: string; suggestions: FocusSuggestion[]; saved: boolean }>('/focus/suggest', { date, save }),
    setFocus: (date: string, node_ids: number[], confirmed = true) => req<FocusDay>('PUT', '/focus', { date, node_ids, confirmed }),

    occurrences: (id: number, from?: string, to?: string) =>
      get<{ occurrences: ApiOccurrence[]; stats: RoutineStats }>(`/routines/${id}/occurrences`, { from, to }),
    setOccurrence: (id: number, date: string, status: 'done' | 'skip' | 'open', note?: string) =>
      post<{ occurrence: ApiOccurrence; stats: RoutineStats }>(`/routines/${id}/occurrences/${date}/${status}`, { note }),

    permissions: (status?: 'pending' | 'approved' | 'rejected') =>
      get<{ permissions: ApiPermission[] }>('/permissions', { status }).then((r) => r.permissions),
    requestPermission: (input: { node_id?: number | null; action: PermissionAction; payload?: unknown; reason?: string }) =>
      post<{ permission: ApiPermission }>('/permissions', input).then((r) => r.permission),
    decidePermission: (id: number, decision: 'approve' | 'reject', note?: string) =>
      post<{ permission: ApiPermission }>(`/permissions/${id}/${decision}`, { note }).then((r) => r.permission),

    views: () => get<{ views: ApiView[] }>('/views').then((r) => r.views),
    createView: (input: { name: string; filter: Record<string, unknown>; icon?: string; pinned?: boolean }) =>
      post<{ view: ApiView }>('/views', input).then((r) => r.view),
    updateView: (id: number, patch: Partial<{ name: string; filter: Record<string, unknown>; icon: string; pinned: boolean; position: number }>) =>
      req<{ view: ApiView }>('PATCH', `/views/${id}`, patch).then((r) => r.view),
    deleteView: (id: number) => req<{ ok: true }>('DELETE', `/views/${id}`),

    dataHealth: () => get<DataHealth>('/health/data'),
    dailyPrep: (date?: string) => get<DailyPrep>('/reviews/daily/prep', { date }),
    weeklyPrep: (date?: string) => get<WeeklyPrep>('/reviews/weekly/prep', { date }),
    reviews: (type?: 'daily_prep' | 'weekly_review', limit?: number) => get<{ reviews: ApiReview[] }>('/reviews', { type, limit }).then((r) => r.reviews),
    createReview: (input: {
      type: 'daily_prep' | 'weekly_review';
      date: string;
      template_id?: number;
      answers: Record<string, unknown>;
      summary_md?: string;
      insights_next_time?: string;
      focus_node_ids?: number[];
      focus_confirmed?: boolean;
    }) => post<{ review: ApiReview; focus: FocusDay | null }>('/reviews', input),
    template: (type: 'daily_prep' | 'weekly_review') => get<{ template: ReviewTemplate }>(`/review-templates/${type}`).then((r) => r.template),
    templateVersions: (type: 'daily_prep' | 'weekly_review') => get<{ versions: ReviewTemplate[] }>(`/review-templates/${type}/versions`).then((r) => r.versions),
    updateTemplate: (type: 'daily_prep' | 'weekly_review', questions: ReviewTemplate['questions'], notes?: string) =>
      post<{ template: ReviewTemplate }>(`/review-templates/${type}`, { questions, notes }).then((r) => r.template),
    undo: (activityId: number) => post<{ undone: number; node: ApiNode }>(`/activity/${activityId}/undo`),

    agentSessions: (limit?: number) => get<{ sessions: ApiSession[] }>('/agent-sessions', { limit }).then((r) => r.sessions),
    createAgentSession: (input: { summary_md: string; decisions_md?: string; node_ids?: number[]; started_at?: string }) =>
      post<{ session: ApiSession }>('/agent-sessions', input).then((r) => r.session),
  };
}

export type GoalBuddyClient = ReturnType<typeof createClient>;
export type { DailyFocus };
